import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { browserHistory } from 'react-router'
import * as storeActions from '../../actions/store'
import { doTrack } from '../../helpers/TaggingSerializer'
import { getGoogleApiKey } from '../../helpers/apiKeyHelpers'

import './StoreDetail.scss'

import Helmet from 'react-helmet'
import NoMatch from '../NoMatch/NoMatch'
import Alert from '@argos/alert'

import {
  BackToStore,
  ClickAndCollect,
  CollectionPoint,
  ErrorBoundary,
  OpeningHours,
  Pagination,
  SearchWithParam,
  SEOText,
  StoreHeadline,
  StoreInfo
} from '../../components'

class StoreDetail extends PureComponent {
  constructor (props, context) {
    super(props, context)
    this.setCurrentFaq = this.setCurrentFaq.bind(this)
    this.getNewStoreDetails = this.getNewStoreDetails.bind(this)
    this._handleKeyLeftRightPress = this._handleKeyLeftRightPress.bind(this)
    this.state = {
      eventListenersSet: false
    }
  }

  static readyOnActions (dispatch, params, req, res) {
    if (params === undefined || params === null) return null
    const storeId = params.storeId.replace(/\D/g, '')
    return Promise.all([dispatch(storeActions.fetchStore(storeId, true, res))])
  }

  componentWillUnmount () {
    if (!this.state.eventListenersSet && this.props.currentStoreIndex !== null) {
      window.removeEventListener('keydown', this._handleKeyLeftRightPress, true)
      this.setState({
        eventListenersSet: true
      })
    }
  }

  componentDidMount () {
    if (global.window) doTrack('ar:storelocator:storeDetails:')
  }

  setCurrentFaq (event, faqId) {
    if (this.props.currentFaq === faqId) {
      this.props.actions.setCurrentFaq(null)
    } else {
      this.props.actions.setCurrentFaq(faqId)
    }
    event.preventDefault()
  }

  _handleKeyLeftRightPress (e) {
    e = e || window.event
    if (this.props.currentStoreIndex === null) return null
    const prevStoreIndex = parseInt(this.props.currentStoreIndex - 1)
    const nextStoreIndex = parseInt(this.props.currentStoreIndex + 1)
    switch (e.keyCode) {
      case 37:
        if (prevStoreIndex < 0) return null
        this.getNewStoreDetails(
          e,
          this.props.stores[prevStoreIndex].id,
          prevStoreIndex,
          this.props.stores[prevStoreIndex]
        )
        browserHistory.push(this.props.stores[prevStoreIndex].permalink)
        break
      case 39:
        if (nextStoreIndex >= this.props.stores.length) return null
        this.getNewStoreDetails(
          e,
          this.props.stores[nextStoreIndex].id,
          nextStoreIndex,
          this.props.stores[nextStoreIndex]
        )
        browserHistory.push(this.props.stores[nextStoreIndex].permalink)
        break
      default:
        break
    }
  }

  getNewStoreDetails (e, newStoreId, newStoreIndex, store) {
    this.props.actions.setCurrentStore(newStoreId, newStoreIndex)
    this.props.actions.fetchStore(newStoreId)
    window.scrollTo(0, 0)
  }

  render () {
    if (!this.props.store && this.props.currentStoreIndex === null) return <NoMatch />
    const store =
      this.props.currentStoreIndex !== null ? this.props.stores[this.props.currentStoreIndex] : this.props.store
    const metaData = {
      title: 'Argos store at ' + store.name,
      meta: [
        {
          name: 'description',
          content: `Find address, contact and opening details for our ${
            store.name
          } store. Buy online for free Fast Track in-store collection. Go Argos.`
        }
      ],
      link: [
        {
          rel: 'canonical',
          href: store.canonical
        }
      ]
    }
    const storeType = store.type === 0 ? 'red' : store.type === 1 ? 'orange' : 'black'
    const icon = `https://media.4rgos.it/i/Argos/icon-pin-${storeType}_2.png?w=45`
    const mapImgSrc = `https://maps.googleapis.com/maps/api/staticmap?center=${store.lat},${
      store.lng
    }&markers=icon:${icon}|${store.lat},${store.lng}&zoom=16&size=640x360&key=${getGoogleApiKey(process.env.GOOGLE_API_ENV)}`

    return (
      <div
        className='store-locator store-detail store-detail-wrapper'
        itemScope
        itemType='http://schema.org/LocalBusiness'
      >
        <Helmet {...metaData} />

        <div className='sd-container container'>
          <div className='row'>
            <div className='xs-12'>
              <BackToStore searchTerm={this.props.searchTerm} />
            </div>
          </div>
          <div className='xs-row md-row'>
            <div className='xs-12 md-6'>
              <a id='storelocatormain' />
              <StoreHeadline {...store} />

              {store.alert && (
                <Alert backgroundColor='white' borderColor='red' textColor='red'>
                  {store.alert}
                </Alert>
              )}
              {store.clickandcollect && <ClickAndCollect />}

              <StoreInfo
                address={store.address}
                town={store.town}
                postcode={store.postcode}
                tel={store.tel}
                storeid={store.id}
              />

              {store.clickAndCollectLocation && <hr />}

              <CollectionPoint location={store.clickAndCollectLocation} />

              <hr />

              <OpeningHours hours={store.storeTimes} tabbable showIcon />

              <hr />
            </div>
            <div className='xs-12 md-6 sd-map-container'>
              <div className='sd-map--static-map-img' style={{ backgroundImage: `url(${mapImgSrc})` }} />
            </div>

            <Pagination
              currentStoreIndex={this.props.currentStoreIndex}
              stores={this.props.stores}
              getStoreCallback={this.getNewStoreDetails}
            />

            <SEOText />
          </div>
        </div>

        <ErrorBoundary>
          <SearchWithParam />
        </ErrorBoundary>
      </div>
    )
  }
}

StoreDetail.propTypes = {
  store: PropTypes.object,
  stores: PropTypes.array,
  currentFaq: PropTypes.number,
  currentStoreIndex: PropTypes.number,
  searchTerm: PropTypes.string,
  actions: PropTypes.object.isRequired
}

function mapStateToProps (state) {
  return {
    loading: state.store.loading,
    error: state.store.error,
    store: state.store.store,
    faqs: state.store.faqs,
    currentLocation: state.locator.currentLocation,
    currentFaq: state.store.currentFaq,
    currentStoreIndex: state.locator.currentStoreIndex,
    stores: state.locator.stores,
    searchTerm: state.locator.searchTerm
  }
}

function mapDispatchToProps (dispatch) {
  return { actions: bindActionCreators(storeActions, dispatch) }
}

export { StoreDetail }

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StoreDetail)



// WEBPACK FOOTER //
// ./src/containers/StoreDetail/StoreDetail.jsx